import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { toast } from 'react-hot-toast';
import CategoryModal from './CategoryModal';

const CategoriesSection = ({ categories, setCategories, globalSearch, refreshTrigger }) => {
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [parentCategory, setParentCategory] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Filter and search categories
  useEffect(() => {
    let filtered = [...categories];

    // Apply search filter
    if (globalSearch?.trim()) {
      filtered = filtered?.filter(category => 
        category?.name?.toLowerCase()?.includes(globalSearch?.toLowerCase()) ||
        category?.description?.toLowerCase()?.includes(globalSearch?.toLowerCase()) ||
        category?.subcategories?.some(sub => 
          sub?.name?.toLowerCase()?.includes(globalSearch?.toLowerCase())
        )
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered?.filter(category => category?.status === filterStatus);
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      if (sortBy === 'name') return a?.name?.localeCompare(b?.name);
      if (sortBy === 'date') return new Date(b?.updatedAt) - new Date(a?.updatedAt);
      if (sortBy === 'status') return a?.status?.localeCompare(b?.status);
      return 0;
    });

    setFilteredCategories(filtered);
    setCurrentPage(1);
  }, [categories, globalSearch, filterStatus, sortBy, refreshTrigger]);

  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setParentCategory(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setParentCategory(null);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleCreateSubcategory = (category) => {
    setSelectedCategory(null);
    setParentCategory(category);
    setModalMode('subcategory');
    setShowModal(true);
  };

  const handleSaveCategory = async (categoryData, mode) => {
    if (mode === 'create') {
      const newCategory = {
        id: Date.now(),
        ...categoryData,
        subcategories: [],
        createdAt: new Date()?.toISOString()?.split('T')?.[0],
        updatedAt: new Date()?.toISOString()?.split('T')?.[0]
      };
      setCategories(prev => [...prev, newCategory]);
    } else if (mode === 'subcategory') {
      const newSubcategory = {
        id: Date.now(),
        name: categoryData?.name,
        status: categoryData?.status,
        parentId: parentCategory?.id
      };
      
      setCategories(prev => prev?.map(cat => 
        cat?.id === parentCategory?.id 
          ? { 
              ...cat, 
              subcategories: [...(cat?.subcategories || []), newSubcategory],
              updatedAt: new Date()?.toISOString()?.split('T')?.[0]
            }
          : cat
      ));
    } else if (mode === 'edit') {
      setCategories(prev => prev?.map(cat => 
        cat?.id === selectedCategory?.id 
          ? { 
              ...cat, 
              ...categoryData,
              updatedAt: new Date()?.toISOString()?.split('T')?.[0]
            }
          : cat
      ));
    }
  };

  const handleDeleteCategory = (categoryId) => {
    if (window?.confirm('Are you sure you want to delete this category?')) {
      setCategories(prev => prev?.filter(cat => cat?.id !== categoryId));
      toast?.success('Category deleted successfully');
    }
  };

  const handleToggleStatus = (categoryId) => {
    setCategories(prev => prev?.map(cat => 
      cat?.id === categoryId 
        ? { ...cat, status: cat?.status === 'active' ? 'inactive' : 'active' }
        : cat
    ));
    toast?.success('Category status updated');
  };

  const handleToggleExpanded = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded?.has(categoryId)) {
      newExpanded?.delete(categoryId);
    } else {
      newExpanded?.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // Pagination
  const totalPages = Math.ceil(filteredCategories?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCategories = filteredCategories?.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="flex-1 overflow-hidden bg-background">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex-shrink-0 bg-card border-b border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Categories & Subcategories</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Manage product categories and their subcategories
              </p>
            </div>
            <button
              onClick={handleCreateCategory}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2"
            >
              <Icon name="Plus" size={16} />
              <span>Add Category</span>
            </button>
          </div>

          {/* Filters */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center space-x-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="name">Sort by Name</option>
                <option value="date">Sort by Date</option>
                <option value="status">Sort by Status</option>
              </select>
            </div>
            
            <div className="text-sm text-muted-foreground">
              {filteredCategories?.length} categories found
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {paginatedCategories?.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
                <Icon name="FolderTree" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No categories found</h3>
              <p className="text-muted-foreground mb-4">
                {globalSearch ? 'No categories match your search criteria.' : 'Get started by creating your first category.'}
              </p>
              {!globalSearch && (
                <button
                  onClick={handleCreateCategory}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Create Category
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {paginatedCategories?.map((category) => (
                <div key={category?.id} className="bg-card border border-border rounded-lg overflow-hidden">
                  {/* Category Header */}
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleToggleExpanded(category?.id)}
                          className="p-1 hover:bg-muted rounded transition-colors"
                        >
                          <Icon 
                            name={expandedCategories?.has(category?.id) ? "ChevronDown" : "ChevronRight"} 
                            size={16} 
                            className="text-muted-foreground" 
                          />
                        </button>
                        <div>
                          <h3 className="font-medium text-foreground">{category?.name}</h3>
                          <p className="text-sm text-muted-foreground">{category?.description}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          category?.status === 'active' ?'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                        }`}>
                          {category?.status}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {category?.subcategories?.length || 0} subcategories
                        </span>
                        
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleCreateSubcategory(category)}
                            className="p-2 hover:bg-muted rounded transition-colors"
                            title="Add Subcategory"
                          >
                            <Icon name="Plus" size={16} className="text-muted-foreground" />
                          </button>
                          <button
                            onClick={() => handleEditCategory(category)}
                            className="p-2 hover:bg-muted rounded transition-colors"
                            title="Edit Category"
                          >
                            <Icon name="Edit2" size={16} className="text-muted-foreground" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(category?.id)}
                            className="p-2 hover:bg-muted rounded transition-colors"
                            title={`${category?.status === 'active' ? 'Deactivate' : 'Activate'} Category`}
                          >
                            <Icon 
                              name={category?.status === 'active' ? "EyeOff" : "Eye"} 
                              size={16} 
                              className="text-muted-foreground" 
                            />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category?.id)}
                            className="p-2 hover:bg-destructive/10 rounded transition-colors"
                            title="Delete Category"
                          >
                            <Icon name="Trash2" size={16} className="text-destructive" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subcategories */}
                  {expandedCategories?.has(category?.id) && (
                    <div className="border-t border-border bg-muted/30">
                      {category?.subcategories?.length > 0 ? (
                        <div className="p-4 space-y-2">
                          {category?.subcategories?.map((subcategory) => (
                            <div key={subcategory?.id} className="flex items-center justify-between py-2 px-3 bg-card rounded border">
                              <div>
                                <span className="text-sm font-medium text-foreground">{subcategory?.name}</span>
                                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                                  subcategory?.status === 'active' ?'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                                }`}>
                                  {subcategory?.status}
                                </span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <button
                                  className="p-1 hover:bg-muted rounded transition-colors"
                                  title="Edit Subcategory"
                                >
                                  <Icon name="Edit2" size={14} className="text-muted-foreground" />
                                </button>
                                <button
                                  className="p-1 hover:bg-destructive/10 rounded transition-colors"
                                  title="Delete Subcategory"
                                >
                                  <Icon name="Trash2" size={14} className="text-destructive" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          No subcategories yet. 
                          <button 
                            onClick={() => handleCreateSubcategory(category)}
                            className="ml-1 text-primary hover:underline"
                          >
                            Add one now
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredCategories?.length)} of {filteredCategories?.length}
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronLeft" size={16} />
                </button>
                <span className="px-4 py-2 text-sm">
                  {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronRight" size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <CategoryModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        mode={modalMode}
        category={selectedCategory}
        parentCategory={parentCategory}
        onSave={handleSaveCategory}
        categories={categories}
      />
    </div>
  );
};

export default CategoriesSection;